import { MouseEvent, ReactNode } from "react";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import Open from "../../../../assets/Open";
import { ReportRow } from "../../../../types";
import { useHasScope } from "../../../../utils/Permission";

type Props =
{
    row: ReportRow
    openInShopify: (shopifyId: string) => void
    selected: boolean
    onToggleSelected: (rowId: number) => void
    isExpanded: boolean
    onToggleDetails: (rowId: number) => void
    renderDetails?: () => ReactNode
}

const formatUpdated = (updated: string) => {
    const date = new Date(updated);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return 'Not available';

    return updated.replace(/T/, ' ').replace(/\..+/, '');
};

const statusLabel = (status: string) => status === 'Available' ? 'Pending' : status;

export default function StoreReportRow({ row, openInShopify, selected, onToggleSelected, isExpanded, onToggleDetails, renderDetails }: Props) {
    const canSeeDetails = useHasScope('reports:view-details');
    const canSelectRows = useHasScope(['stores:create', 'write-force-sync'], true);

    const showDetails = canSeeDetails && renderDetails !== undefined;
    const stopRowToggle = (e: MouseEvent) => e.stopPropagation();

    return (
        <div className="list-item">
             <div
                className={`item-line ${showDetails ? 'is-expandable' : ''}`}
                onClick={showDetails ? () => onToggleDetails(row.id) : undefined}
             >
                {canSelectRows && (
                    <div className="item-column selectItem" onClick={stopRowToggle}>
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => onToggleSelected(row.id)}
                            aria-label={`Select ${row.name}`}
                        />
                    </div>
                )}
                <div className="item-column nameItem">{row.name ? row.name : 'Not available'}</div>
                {canSeeDetails && <div className="item-column idItem">{row.id ? row.id : 'Not available'}</div>}
                <div className="item-column shopifyItem">
                    {row.shopifyId ? (
                        <>
                            {row.shopifyId}
                            <button
                                className="open-shopify-button"
                                title="Open in Shopify"
                                onClick={(e) => { stopRowToggle(e); openInShopify(row.shopifyId); }}
                            >
                                <Open></Open>
                            </button>
                        </>
                    ) : 'Not available'}
                </div>
                <div className="item-column productItem">{row.handle ? row.handle : 'Not available'}</div>
                <div className={`item-column statusItem ${row.status === 'Error' ? 'is-error' : ''}`}>
                    <span className="report-status">{statusLabel(row.status)}</span>
                </div>
                {canSeeDetails && (
                    <div className="item-column actionItem">{row.action ? row.action : 'Not available'}</div>
                )}
                <div className="item-column updatedItem">{formatUpdated(row.updated)}</div>
                {showDetails && (
                    <div className="item-column detailItem">
                        <button
                            type="button"
                            className={`report-row-toggle ${isExpanded ? 'is-expanded' : ''}`}
                            onClick={(e) => { stopRowToggle(e); onToggleDetails(row.id); }}
                            aria-expanded={isExpanded}
                            aria-label="Details"
                        >
                            <ChevronRightRoundedIcon fontSize="small" />
                        </button>
                    </div>
                )}
            </div>
            {showDetails && isExpanded && (
                <div className="product-details-row">
                    {renderDetails?.()}
                </div>
            )}
        </div>
    )
}